import {
  buildServiceTree,
  indexIncluded,
  mapPrice,
  resolveRef,
  ServiceTreeBuilders
} from 'Douleutaras/functional-components/ServicePrices/servicePriceTree';

import { isLabelResource, isPriceResource } from './servicePriceCatalog';
import {
  SettingsService,
  SettingsServicePriceResource,
  SettingsServicePricesDocument,
  SettingsServicesCategory
} from './types';

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const settingsBuilders: ServiceTreeBuilders<SettingsServicePriceResource, SettingsService, SettingsServicesCategory> = {
  createNode: ({ serviceId, level, label }) => ({ serviceId, level, label, isHidden: false }),
  applyRow: (node, { id, attributes }) => ({
    ...node,
    priceId: id,
    isHidden: attributes.hidden,
    price: attributes.status === 'priced' ? mapPrice(attributes) : undefined,
    marketRange: attributes.marketRange ?? undefined,
    marketRangeLabel: attributes.marketRangeLabel ?? undefined
  }),
  // Raw id, as the catalog lines carry it
  createCategory: (category, _categoryId, services) => ({ categoryId: category.id, title: category.title, services })
};

/**
 * Maps the pro's `user/settings/service-prices/` document into the settings accordions: every
 * vetted category in API order (empty ones included), its prices oldest first, details nested
 * under their service.
 */
export const mapServicePriceSettings = (doc: SettingsServicePricesDocument): Array<SettingsServicesCategory> => {
  const included = doc.included ?? [];
  const priceById = new Map(included
    .filter(isPriceResource)
    .map(price => [price.id, price]));
  // Categories come in `data`, so they are indexed alongside the sideloaded labels
  const resourceByRef = indexIncluded([...doc.data, ...included.filter(isLabelResource)]);

  const seedCategories = doc.data
    .map(category => resolveRef(resourceByRef, { data: { type: category.type, id: category.id } }))
    .filter(isDefined);

  const rows = doc.data
    .flatMap(category => category.relationships?.prices?.data ?? [])
    .map(link => priceById.get(link.id))
    .filter(isDefined);

  return buildServiceTree(rows, resourceByRef, settingsBuilders, seedCategories);
};
